import axios from "axios";
import { clearApiCache } from "./apiService";

// Base URL de l'API
const resolveApiBaseUrl = () => {
  const baseUrl =
    process.env.REACT_APP_API_URL ||
    '/api';

  if (
    typeof window !== 'undefined' &&
    window.location?.protocol === 'https:' &&
    typeof baseUrl === 'string' &&
    baseUrl.startsWith('http://')
  ) {
    return '/api';
  }

  return baseUrl;
};

const API_BASE_URL = resolveApiBaseUrl();

// Repli pour les API antérieures à la règle `unique` sur customers.email :
// l'email déjà pris y remonte en violation de contrainte, pas en erreur de validation.
const isDuplicateEmailError = (data) => {
  const haystack = [data?.message, data?.exception, data?.error]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (
    haystack.includes('customers_email_unique') ||
    (haystack.includes('email') &&
      (haystack.includes('unique') || haystack.includes('duplicate')))
  );
};

// Instance Axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Intercepteur pour ajouter automatiquement le token JWT si présent
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

class AuthService {
  // 🔹 Installe une session (access_token + user) reçue soit du login, soit
  // de la confirmation d'un lien de réinitialisation — même effet dans les
  // deux cas : le client se retrouve connecté.
  _persistSession(data) {
    if (!data.access_token) return;

    // Les lectures faites avant la connexion étaient anonymes : les garder
    // ferait voir au nouveau connecté le catalogue du visiteur.
    clearApiCache();

    localStorage.setItem("authToken", data.access_token);

    if (data.user) {
      localStorage.setItem("userData", JSON.stringify(data.user));
    }

    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("auth:login"));
    }
  }

  // 🔹 Connexion (adapté à l'API)
  async login({ email, password, username }) {
    try {
      // L'API accepte "identity" (email ou username) et "password"
      const identity = email || username;

      const response = await api.post("/auth/customers/login", {
        identity,
        password
      });

      // L'API retourne { access_token, user }
      this._persistSession(response.data);

      return response.data;
    } catch (error) {
      if (error.response?.data) {
        const apiError = new Error(error.response.data.message || "Erreur lors de la connexion");
        apiError.status = error.response.status;
        if (error.response.status === 422 && error.response.data.errors) {
          apiError.validationErrors = error.response.data.errors;
        }
        apiError.response = error.response;
        throw apiError;
      }
      throw new Error("Erreur lors de la connexion");
    }
  }

  // 🔹 Déconnexion (adapté à l'API)
  async logout() {
    try {
      // Appel à l'API pour déconnecter côté serveur
      await api.get("/auth/customers/logout");
    } catch (error) {
      console.warn("⚠️ Erreur lors de la déconnexion côté serveur:", error);
    } finally {
      // Nettoyage local dans tous les cas
      clearApiCache();
      localStorage.removeItem("authToken");
      localStorage.removeItem("userData");
      // Vider le panier local pour ne pas le transmettre au prochain utilisateur
      localStorage.removeItem("yiatou_cart");
      // Idem pour l'état d'onboarding et le profil de paiement : ils décrivent
      // un compte, pas un navigateur. Les laisser en place fait croire au
      // suivant que ses informations sont déjà renseignées, et les écrans qui
      // devraient les réclamer restent muets.
      localStorage.removeItem("yiatou_onboarding");
      localStorage.removeItem("yiatou_payment_profile");
      window.location.href = "/";
    }
  }

  // 🔹 Modifier le mot de passe
  async changePassword({ currentPassword, newPassword, confirmPassword }) {
    try {
      const response = await api.put("/auth/customers/edit/password", {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword
      });
      return response.data;
    } catch (error) {
      if (error.response?.data) {
        const apiError = new Error(
          error.response.data.message || "Erreur lors de la modification du mot de passe"
        );

        apiError.status = error.response.status;

        if (error.response.status === 422 && error.response.data.errors) {
          apiError.validationErrors = error.response.data.errors;
        }

        apiError.response = error.response;
        throw apiError;
      }

      throw new Error("Erreur lors de la modification du mot de passe");
    }
  }

  /**
   * Demande l'envoi d'un lien de réinitialisation par email
   */
  async resetPassword(email) {
    return api.put('/auth/customers/password-reset', { email });
  }

  /**
   * Confirme le lien reçu par email (token + nouveau mot de passe) et
   * installe la session retournée : le client repart connecté, sans avoir à
   * ressaisir un mot de passe qu'il vient tout juste de choisir.
   */
  async confirmResetPassword({ email, token, password, password_confirmation }) {
    try {
      const response = await api.put('/auth/customers/password-reset/confirm', {
        email,
        token,
        password,
        password_confirmation,
      });

      this._persistSession(response.data);

      return response.data;
    } catch (error) {
      if (error.response?.data) {
        const apiError = new Error(
          error.response.data.message || "Erreur lors de la réinitialisation du mot de passe"
        );
        apiError.status = error.response.status;
        if (error.response.status === 422 && error.response.data.errors) {
          apiError.validationErrors = error.response.data.errors;
        }
        apiError.response = error.response;
        throw apiError;
      }
      throw new Error("Erreur lors de la réinitialisation du mot de passe");
    }
  }

  /**
   * Inscription d'un nouvel utilisateur (POST /customers, endpoint public)
   *
   * Le formulaire se limite à civilité, prénom, email et mot de passe : c'est
   * tout ce qu'il faut pour ouvrir un compte et s'y reconnecter. Le nom, les
   * numéros et l'adresse se collectent plus tard, chacun au moment où il sert —
   * la livraison pour l'adresse et le téléphone, le paiement ou le retrait pour
   * le Mobile Money. Les champs restent acceptés ici, pour un appel qui les
   * fournirait, mais aucun n'est exigé.
   */
  async register(userData) {
    try {
      const payload = {
        first_name: userData.first_name?.trim() || null,
        last_name: userData.last_name?.trim() || null,
        email: userData.email?.trim() || null,
        phone: userData.phone?.replace(/\s/g, '') || null,
        mobile_money_phone: userData.mobile_money_phone?.replace(/\s/g, '') || null,
        address: userData.address?.trim() || null,
        gender: userData.gender || userData.civility || null,
        password: userData.password,
        password_confirmation: userData.password_confirmation,
      };

      const response = await api.post('/customers', payload);
      return response.data;
    } catch (error) {
      if (error.response?.data) {
        const apiError = new Error(error.response.data.message || "Erreur lors de l'inscription");
        apiError.status = error.response.status;

        if (error.response.status === 422 && error.response.data.errors) {
          apiError.validationErrors = error.response.data.errors;
        }

        // Filet de sécurité : sur une API pas encore à jour, le doublon d'email
        // remonte en 500 (violation de contrainte) au lieu d'un 422.
        if (error.response.status >= 500 && isDuplicateEmailError(error.response.data)) {
          apiError.message = 'Un compte existe déjà avec cet email';
          apiError.validationErrors = {
            email: ['Un compte existe déjà avec cet email'],
          };
          apiError.status = 422;
        }

        apiError.response = error.response;
        throw apiError;
      }

      throw new Error("Erreur lors de l'inscription");
    }
  }

  // 🔹 Récupérer le token
  getToken() {
    return localStorage.getItem("authToken");
  }

  // 🔹 Vérifier si l'utilisateur est authentifié
  // Note: les tokens Sanctum sont opaques (pas JWT), on vérifie juste la présence
  isAuthenticated() {
    const token = this.getToken();
    return !!token && token.length > 0;
  }

  // 🔹 Récupérer les infos utilisateur complètes (adapté au format API)
  getUser() {
    // 1. Depuis localStorage
    const storedUserData = localStorage.getItem("userData");
    if (storedUserData) {
      try {
        const userData = JSON.parse(storedUserData);

        // Construire une base commune pour les différents types d'utilisateurs
        const baseUser = {
          // Données de base utilisateur
          id: userData.id || null,
          email: userData.email || null,
          username: userData.username || null,
          avatar: userData.avatar || null,
          passwordChanged: userData.password_changed || false,
          lastLogin: userData.last_login || null,
          
          // Données de la personne liée (schéma User classique)
          person: userData.person ? {
            id: userData.person.id || null,
            name: userData.person.name || null,
            firstname: userData.person.firstname || null,
            maidenName: userData.person.maiden_name || null,
            nip: userData.person.nip || null,
            birthdate: userData.person.birthdate || null,
            gender: userData.person.gender || null,
            phone: userData.person.phone || null,
          } : null,
          
          // Rôle et permissions
          role: userData.role || null,
          permissions: userData.permissions || null,
        };

        // Adaptation pour le schéma CustomerResource (compte client/marchand)
        const isCustomerResource =
          userData.first_name !== undefined ||
          userData.last_name !== undefined ||
          userData.mobile_money_phone !== undefined ||
          userData.address !== undefined ||
          userData.phone !== undefined;

        if (isCustomerResource) {
          baseUser.firstName = userData.first_name || null;
          baseUser.lastName = userData.last_name || null;
          baseUser.phone = userData.phone || null;
          baseUser.address = userData.address || null;
          baseUser.mobileMoneyPhone = userData.mobile_money_phone || null;
          baseUser.mobile_money_phone = userData.mobile_money_phone || null;
          baseUser.moov_money_phone = userData.moov_money_phone || null;
          // Dossier de versement : lu au retrait de commission pour savoir s'il
          // reste quelque chose à demander au client. Titulaires distincts par
          // opérateur — jamais le même champ pour les deux.
          baseUser.mobileMoneyAccountName = userData.mobile_money_account_name || null;
          baseUser.moovMoneyAccountName = userData.moov_money_account_name || null;
          baseUser.activityProfile = userData.activity_profile || null;
          baseUser.dateOfBirth = userData.date_of_birth || null;
          baseUser.affiliateCode = userData.affiliate_code || null;
          baseUser.ordersCount = userData.orders_count || 0;
          baseUser.orders = userData.orders || [];
          baseUser.commissions = userData.commissions || [];
        }

        return baseUser;
      } catch (error) {
        console.error("❌ Erreur parsing userData:", error);
      }
    }

    // 2. Fallback: JWT (données de base seulement)
    const token = this.getToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));

      return {
        id: payload.id || payload.userId || payload.sub || null,
        email: payload.email || null,
        username: payload.username || null,
        avatar: null,
        passwordChanged: false,
        lastLogin: null,
        person: null,
        role: payload.role || null,
        permissions: null,
      };
    } catch (error) {
      console.error("❌ Erreur JWT:", error);
      return null;
    }
  }

  // 🔹 Mettre à jour les infos utilisateur stockées localement
  updateLocalUser(newData) {
    const storedUserData = localStorage.getItem("userData");
    let userData = {};

    if (storedUserData) {
      try {
        userData = JSON.parse(storedUserData);
      } catch {
        console.warn("⚠️ userData corrompu, réinitialisation...");
      }
    }

    // Fusionner les nouvelles données
    const updatedUser = { ...userData, ...newData };

    // Sauvegarder en localStorage
    localStorage.setItem("userData", JSON.stringify(updatedUser));

    return updatedUser;
  }

  // 🔹 Récupérer le profil utilisateur depuis l'API
  async fetchUserProfile(userId) {
    try {
      const response = await api.get(`/users/${userId}`);
      
      if (response.data.data) {
        // Mettre à jour le localStorage avec les données fraîches
        this.updateLocalUser(response.data.data);
        return response.data.data;
      }
      
      return null;
    } catch (error) {
      console.error("❌ Erreur lors de la récupération du profil:", error);
      throw error;
    }
  }

  // 🔹 Vérifier si l'utilisateur a une permission spécifique
  hasPermission(permissionCode) {
    const user = this.getUser();
    if (!user || !user.permissions) return false;

    // Si permissions est une chaîne, la parser
    if (typeof user.permissions === 'string') {
      try {
        const perms = JSON.parse(user.permissions);
        return perms.some(p => p.code === permissionCode);
      } catch {
        return false;
      }
    }

    // Si permissions est déjà un tableau
    if (Array.isArray(user.permissions)) {
      return user.permissions.some(p => p.code === permissionCode);
    }

    return false;
  }

  // 🔹 Vérifier si l'utilisateur a un rôle spécifique
  hasRole(roleCode) {
    const user = this.getUser();
    if (!user || !user.role) return false;

    return user.role.code === roleCode;
  }
}

const authServiceInstance = new AuthService();
export default authServiceInstance;